/**
 * @file A non-blocking "Verify your email" banner for signed-in people whose address is not yet
 * confirmed. Nothing is gated on verification (it is sent, not required), so this only nudges: the
 * app underneath stays fully usable. Not rendered for verified users, or while there is no user.
 */
import { useState } from "react";
import { useAuthValue } from "../auth/context";
import { SecondaryButton } from "./ui";

export function VerifyEmailBanner() {
  const { session, actions } = useAuthValue();
  const [state, setState] = useState<"idle" | "sending" | "sent">("idle");
  const [error, setError] = useState<string | null>(null);

  const user = session.user;
  if (user === null || user.emailVerified) return null;

  async function resend(email: string): Promise<void> {
    if (state === "sending") return;
    setState("sending");
    setError(null);
    try {
      const failure = await actions.sendVerificationEmail(email);
      if (failure === null) {
        setState("sent");
      } else {
        setError(failure);
        setState("idle");
      }
    } catch {
      setError("Could not reach the server. Check your connection and try again.");
      setState("idle");
    }
  }

  return (
    <aside
      aria-label="Account notice"
      className="border-b border-border bg-muted px-4 py-2.5 sm:px-6 lg:px-8"
    >
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
        <p>
          <strong className="font-medium">Verify your email.</strong>{" "}
          We sent a confirmation link to {user.email}.
        </p>
        {state === "sent" ? (
          <p role="status">Email sent. Check your inbox.</p>
        ) : (
          <SecondaryButton onClick={() => void resend(user.email)} disabled={state === "sending"}>
            {state === "sending" ? "Sending…" : "Resend email"}
          </SecondaryButton>
        )}
        {error === null ? null : (
          <p role="alert" className="text-destructive">
            {error}
          </p>
        )}
      </div>
    </aside>
  );
}
